import { useState, useEffect } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { useAuth } from "../modules/admin/AuthContext";

function Navbar({ cartCounter }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { isLogIn, cerrarSesion } = useAuth();

  const tieneHeroDetras = pathname === "/";
  const navbarOscuro = scrolled || !tieneHeroDetras;

  // escucho el scroll para que el navbar cambie de estilo al bajar
  useEffect(() => {
    function alScrollear() {
      setScrolled(window.scrollY > 20);
    }
    window.addEventListener("scroll", alScrollear);
    return () => window.removeEventListener("scroll", alScrollear);
  }, []);

  const navLinks = [
    { name: "Inicio", path: "/" },
    { name: "Cómo comprar", path: "/como-comprar" },
    { name: "Contacto", path: "/contacto" },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 py-4 transition-all duration-500 ${
        navbarOscuro
          ? "bg-black/95 backdrop-blur-lg shadow-lg shadow-black/20"
          : "bg-black/10 backdrop-blur-sm"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-12 flex items-center justify-between">

        {/* boton hamburguesa mobile */}
        <button
          className="md:hidden text-white focus:outline-none z-50 p-1"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Menú"
        >
          <div className="w-5 flex flex-col gap-1.5">
            <span
              className={`block h-[2px] bg-white transition-transform duration-200 ${
                menuOpen ? "rotate-45 translate-y-[7.5px]" : ""
              }`}
            />
            <span
              className={`block h-[2px] bg-white transition-opacity duration-200 ${
                menuOpen ? "opacity-0" : ""
              }`}
            />
            <span
              className={`block h-[2px] bg-white transition-transform duration-200 ${
                menuOpen ? "-rotate-45 -translate-y-[7.5px]" : ""
              }`}
            />
          </div>
        </button>

        {/* marca y logo */}
        <Link
          to="/"
          className="font-sans leading-[0.85] uppercase flex flex-col"
        >
          <span className="font-light text-white text-lg tracking-tight">TU</span>
          <span className="font-bold text-brand text-lg tracking-tight">COMPRA</span>
        </Link>

        {/* links del menu */}
        <div
          className={`
            fixed md:static top-0 left-0 w-full h-screen md:h-auto
            bg-black/85 backdrop-blur-xl md:bg-transparent md:backdrop-blur-none
            flex flex-col md:flex-row items-center justify-center gap-8 md:gap-10
            transition-opacity duration-200 z-40
            ${
              menuOpen
                ? "opacity-100 pointer-events-auto"
                : "opacity-0 pointer-events-none md:opacity-100 md:pointer-events-auto"
            }
          `}
        >
          {navLinks.map((link) => (
            <NavLink
              key={link.path}
              to={link.path}
              end={link.path === "/"}
              onClick={() => setMenuOpen(false)}
              className={({ isActive }) =>
                `font-sans font-medium text-lg md:text-xs tracking-wider uppercase transition-colors pb-1 border-b-2 ${
                  isActive
                    ? "text-brand border-brand"
                    : "text-white border-transparent hover:text-brand"
                }`
              }
            >
              {link.name}
            </NavLink>
          ))}

          {isLogIn && (
            <NavLink
              to="/admin/dashboard"
              onClick={() => setMenuOpen(false)}
              className={({ isActive }) =>
                `font-sans font-semibold text-lg md:text-xs tracking-wider uppercase transition-colors pb-1 border-b-2 ${
                  isActive
                    ? "text-brand border-brand"
                    : "text-white border-transparent hover:text-brand"
                }`
              }
            >
              Dashboard
            </NavLink>
          )}

          {/* accion mobile */}
          <div className="md:hidden mt-6">
            {isLogIn ? (
              <button
                onClick={() => {
                  cerrarSesion();
                  setMenuOpen(false);
                }}
                className="text-red-500 font-sans text-xs uppercase tracking-wider underline underline-offset-4"
              >
                Salir
              </button>
            ) : (
              <Link
                to="/admin/login"
                onClick={() => setMenuOpen(false)}
                className="text-white bg-brand font-sans text-xs font-medium uppercase tracking-wider px-8 py-3 block transition-colors"
              >
                Ingresar
              </Link>
            )}
          </div>
        </div>

        {/* acciones derechas */}
        <div className="flex items-center gap-6">
          {!isLogIn && (
            <Link
              to="/carrito"
              className="relative text-white hover:text-brand transition-colors p-1 flex items-center gap-1.5"
              aria-label="Carrito de compras"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.8}
                stroke="currentColor"
                className="w-5 h-5"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15.75 10.5V6a3.75 3.75 0 1 0-7.5 0v4.5m11.356-1.993 1.263 12c.07.665-.45 1.243-1.119 1.243H4.25a1.125 1.125 0 0 1-1.12-1.243l1.264-12A1.125 1.125 0 0 1 5.513 7.5h12.974c.576 0 1.059.435 1.119 1.007ZM8.25 10.5a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Zm7.5 0a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Z"
                />
              </svg>

              <AnimatePresence>
                {cartCounter > 0 && (
                  <span className="font-sans text-xs font-bold text-brand">
                    ({cartCounter})
                  </span>
                )}
              </AnimatePresence>
            </Link>
          )}

          {/* estado de sesion o ingresar en desktop */}
          <div className="hidden md:block">
            {isLogIn ? (
              <button
                onClick={() => {
                  cerrarSesion();
                  navigate("/admin/login");
                }}
                className="flex items-center gap-2 text-xs font-sans text-red-500 font-medium hover:text-red-400 tracking-wider uppercase transition-colors"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.8}
                  stroke="currentColor"
                  className="w-4 h-4"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15.75 9V5.25A2.25 2.25 0 0 0 13.5 3h-6a2.25 2.25 0 0 0-2.25 2.25v13.5A2.25 2.25 0 0 0 7.5 21h6a2.25 2.25 0 0 0 2.25-2.25V15m3 0 3-3m0 0-3-3m3 3H9"
                  />
                </svg>
                Salir
              </button>
            ) : (
              <Link
                to="/admin/login"
                className="text-xs font-sans text-white bg-brand hover:bg-[#A3740A] px-5 py-2 transition-colors uppercase tracking-wider font-medium"
              >
                Ingresar
              </Link>
            )}
          </div>
        </div>

      </div>
    </nav>
  );
}

export default Navbar;
